import { forwardRef, type ReactNode, type InputHTMLAttributes, type TextareaHTMLAttributes, type SelectHTMLAttributes } from 'react';
import { Check } from 'lucide-react';
import type { WorkspaceColor } from '@unitly/contracts';

export const wsColor = (c: WorkspaceColor | string | undefined) => `var(--ws-${c ?? 'indigo'})`;

/** Square icon glyph only (sidebar, small brand moments). Sizing/glow comes from the `.brand-mark`/`.brand-mark.sm` classes on the wrapper. */
export function BrandIcon({ className }: { className?: string }) {
  return <img src="/icon-192.png" alt="" className={className} style={{ width: '100%', height: '100%', borderRadius: 'inherit' }} />;
}

/** Full "Unitly" wordmark, swapping black/white text for light/dark so it stays legible in both themes. */
export function BrandLogo({ height = 32 }: { height?: number }) {
  return (
    <span className="brand-logo" style={{ height }} aria-label="Unitly">
      <img src="/brand/logo-black.png" alt="" className="brand-logo-light" />
      <img src="/brand/logo-white.png" alt="" className="brand-logo-dark" />
    </span>
  );
}

const AVATAR_COLORS: WorkspaceColor[] = ['indigo', 'blue', 'teal', 'green', 'orange', 'pink', 'purple', 'red', 'amber', 'lime'];
function hash(s: string) {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}
export const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]!.toUpperCase()).join('') || '?';

export function Avatar({ name, id, size = 32, avatarUrl }: { name: string; id?: string; size?: number; avatarUrl?: string | null }) {
  const color = AVATAR_COLORS[hash(id ?? name) % AVATAR_COLORS.length];
  if (avatarUrl) {
    return <img className="avatar avatar-img" src={avatarUrl} alt="" style={{ '--size': `${size}px` } as React.CSSProperties} />;
  }
  return (
    <span className="avatar" style={{ '--size': `${size}px`, '--avatar-bg': wsColor(color) } as React.CSSProperties} aria-hidden="true">
      <span className="avatar-text">{initials(name)}</span>
    </span>
  );
}

export function AvatarStack({ people, max = 3, size = 22 }: { people: { id: string; displayName: string; avatarUrl?: string | null }[]; max?: number; size?: number }) {
  if (!people.length) return null;
  return (
    <span className="avatar-stack" title={people.map((p) => p.displayName).join(', ')}>
      {people.slice(0, max).map((p) => (
        <Avatar key={p.id} id={p.id} name={p.displayName} size={size} avatarUrl={p.avatarUrl} />
      ))}
      {people.length > max && <span className="avatar" style={{ '--size': `${size}px`, '--avatar-bg': 'var(--label-3)' } as React.CSSProperties}>+{people.length - max}</span>}
    </span>
  );
}

export function WorkspaceAvatar({ name, keyLabel, color, active, size = 66, logoUrl }: { name: string; keyLabel: string; color: WorkspaceColor; active?: boolean; size?: number; logoUrl?: string | null }) {
  return (
    <span className={`story-ring${active ? ' active' : ''}`} style={{ '--ring-color': wsColor(color), width: size, height: size } as React.CSSProperties} aria-hidden="true">
      <span className="story-inner">
        {logoUrl ? (
          <img className="story-avatar story-logo" src={logoUrl} alt="" title={name} />
        ) : (
          <span className="story-avatar" style={{ fontSize: size * 0.24 }} title={name}>{keyLabel.slice(0, 3)}</span>
        )}
      </span>
    </span>
  );
}

export function Spinner() {
  return <span className="spinner" role="status" aria-label="Caricamento" />;
}

export function PageLoader() {
  return (
    <div className="center">
      <Spinner />
    </div>
  );
}

export function EmptyState({ icon, title, children, tone = 'green' }: { icon?: ReactNode; title: string; children?: ReactNode; tone?: 'green' | 'neutral' }) {
  return (
    <div className="empty">
      <span className="icon" style={tone === 'neutral' ? { background: 'var(--fill)', color: 'var(--label-2)' } : undefined}>
        {icon ?? <Check size={22} strokeWidth={2.5} />}
      </span>
      <strong>{title}</strong>
      {children && <p>{children}</p>}
    </div>
  );
}

export function Segmented<T extends string>({ value, onChange, options, label }: { value: T; onChange: (v: T) => void; options: { value: T; label: string }[]; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={value === o.value} onClick={() => onChange(o.value)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

interface FieldProps { label: string; error?: string; hint?: string }

export const TextField = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement> & FieldProps>(function TextField(
  { label, error, hint, ...props },
  ref,
) {
  return (
    <label className="field">
      <span>{label}</span>
      <input ref={ref} className="input" aria-invalid={Boolean(error)} {...props} />
      {error ? <span className="field-error">{error}</span> : hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
});

export function TextArea({ label, error, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & FieldProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <textarea className="textarea" aria-invalid={Boolean(error)} {...props} />
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}

export function SelectField({ label, error, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & FieldProps) {
  return (
    <label className="field">
      <span>{label}</span>
      <select className="select" {...props}>{children}</select>
      {error && <span className="field-error">{error}</span>}
    </label>
  );
}
